import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import {
  bulkCapacityTemplateUrl,
  uploadBulkCapacitySchedule,
} from "../requests/location-requests";
import { fetchDownloadFile } from "../../../util/FileUtils";
import type { CapacityUploadResult } from "../../../types/LocationCapacitySchedule";
import { getErrorMessage } from "../../../util/NetworkErrorUtil";

interface GlobalCapacityBulkUploadProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful upload so the parent can refresh the location list. */
  onUploaded: (result: CapacityUploadResult) => void;
  onError: (message: string) => void;
}

/**
 * POC: global capacity bulk upload. Suppliers download one template pre-filled
 * with every location's id and name, enter schedules for as many locations as
 * they like, and upload a single CSV that updates all of them at once.
 */
const GlobalCapacityBulkUpload = ({
  open,
  onClose,
  onUploaded,
  onError,
}: GlobalCapacityBulkUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<CapacityUploadResult | null>(null);

  const handleFile = async (file: File) => {
    setFileName(file.name);
    setUploading(true);
    setResult(null);
    try {
      const response = await uploadBulkCapacitySchedule(file);
      const body = response.body as CapacityUploadResult;
      setResult(body);
      onUploaded(body);
    } catch (err) {
      onError(getErrorMessage(err) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    setFileName(null);
    setResult(null);
    onClose();
  };

  const handleDownloadTemplate = () => {
    fetchDownloadFile(
      bulkCapacityTemplateUrl(),
      undefined,
      (err) => onError(getErrorMessage(err) || "Could not download the template"),
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Upload Capacity</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: "#5E7782", mb: 2 }}>
          Update the capacity schedules for all of your locations in one file.
          Download the template below — it is pre-filled with each location's id
          and name. For every location you want to schedule, fill in the{" "}
          <strong>effective_date, end_date, capacity</strong> and (optionally){" "}
          <strong>note</strong> columns, adding extra rows with the same
          location id for additional date ranges. Leave a location's rows blank
          to leave its schedule unchanged. Uploading replaces the schedule for
          each location that appears with data. Keep the{" "}
          <strong>schedule_version</strong> column as downloaded: if a
          location's schedule has been changed since then, that location is
          skipped, so download a fresh template and try again.
          Uploading replaces the schedule from today on; rows that have already
          started are kept.
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
            }}
          />
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? "Uploading…" : "Choose CSV file"}
          </Button>
          <Link
            component="button"
            type="button"
            onClick={handleDownloadTemplate}
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.85rem" }}
          >
            <DownloadIcon fontSize="small" /> Download template
          </Link>
          {fileName && (
            <Typography variant="caption" sx={{ color: "#8299A5" }}>
              {fileName}
            </Typography>
          )}
        </Box>

        {result && (
          <Alert
            severity={result.errors.length > 0 ? "warning" : "success"}
            sx={{ mt: 2 }}
          >
            Processed {result.rowsProcessed} row(s), accepted {result.rowsAccepted}.
            {result.errors.length > 0 && (
              <List dense sx={{ mt: 0.5, listStyleType: "disc", pl: 3 }}>
                {result.errors.map((message, i) => (
                  <ListItem key={i} sx={{ display: "list-item", p: 0 }}>
                    <Typography variant="caption">{message}</Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GlobalCapacityBulkUpload;
